import { ApiResult, HttpClient } from "../http.js";
import { AssetMaintenanceScheduleDto, AssetMaintenanceScheduleStatus, CustomerServiceAgreementsDto } from "../types/assets.js";
import { CustomerDto, CustomerEmailLabel, CustomerHubDto, CustomerMarginOverrides, PagedCustomers } from "../types/customers.js";

/**
 * Full customer field set shared by create + update payloads, mirroring the .NET
 * Create/Update commands (a field-for-field port of TS `CreateCustomerModel`).
 */
export interface CustomerFields {
  // Structured address
  street?: string | null;
  zipCode?: string | null;
  city?: string | null;
  countryKey?: string | null;
  // Business identification
  isPerson?: boolean;
  vatNumber?: string | null;
  eanNumber?: string | null;
  seNumber?: string | null;
  pNumber?: string | null;
  // Contact details
  attPerson?: string | null;
  webpage?: string | null;
  // Pricing
  defaultHourlyRate?: number | null;
  materialPriceAdjustment?: number | null;
  /** @deprecated Use materialPriceAdjustment instead */
  materialDiscount?: number | null;
  /** Per-group margin overrides. Most specific match wins. */
  marginOverrides?: CustomerMarginOverrides | null;
  // Payment terms
  paymentConditionType?: string | null;
  paymentConditionNumberOfDays?: number | null;
  // Dinero-specific
  dineroContactGuid?: string | null;
  dineroIsDebitor?: boolean | null;
  dineroIsCreditor?: boolean | null;
  externalReference?: string | null;
  isMember?: boolean | null;
  memberNumber?: string | null;
  companyTypeKey?: string | null;
  // E-conomic-specific
  economicCurrency?: string | null;
  economicCustomerGroupNumber?: number | null;
  economicPaymentTermsNumber?: number | null;
  economicVatZoneNumber?: number | null;
  economicCreditLimit?: number | null;
  economicBalance?: number | null;
  economicDueAmount?: number | null;
  economicBarred?: boolean | null;
  // Legacy / deprecated aliases
  /** @deprecated Use eanNumber instead */
  ean?: string | null;
  /** @deprecated Use vatNumber instead */
  corporateIdentificationNumber?: string | null;
  /** @deprecated Use webpage instead */
  website?: string | null;
  // Customer portal
  portalEnabled?: boolean | null;
  portalEmail?: string | null;
  lastPortalAccess?: string | null;
  /** Samarbejdspartner (#4556). Omit to leave unchanged. */
  isPartner?: boolean | null;
  /** "Rekvireret af" (#4556). On update: `null` clears it, omitted leaves it. */
  sourcePartnerCustomerId?: string | null;
  /**
   * Every email address on the customer. Omit the field to leave the stored list
   * untouched; send `[]` to clear it. `contactEmail` mirrors the primary entry.
   */
  emails?: CustomerEmailPayload[];
  /**
   * Delivery addresses. Omit the field to leave the stored list untouched; send `[]` to
   * clear it — the same contract as `emails`.
   */
  deliveryAddresses?: CustomerPartyPayload[];
  /** Contact people. Same omit-vs-empty contract as `deliveryAddresses`. */
  contacts?: CustomerPartyPayload[];
  /** Agreed hourly prices. Same omit-vs-empty contract as `deliveryAddresses`. */
  hourlyPrices?: CustomerHourlyPricePayload[];
}

/** Write shape for one agreed hourly price. Blank names are dropped server-side. */
export interface CustomerHourlyPricePayload {
  name: string;
  itemNumber?: string | null;
  listPrice?: number | null;
}

/** Write shape for one delivery address or contact person. Blank names are dropped server-side. */
export interface CustomerPartyPayload {
  name: string;
  attPerson?: string | null;
  street?: string | null;
  zipCode?: string | null;
  city?: string | null;
  phone?: string | null;
  mobile?: string | null;
  email?: string | null;
  eanNumber?: string | null;
}

export interface CustomerEmailPayload {
  address: string;
  /** `invoice` / `contact` / `other`. Unknown values fall back to `other` server-side. */
  label?: CustomerEmailLabel;
  isPrimary?: boolean;
}

export interface CreateCustomerPayload extends CustomerFields {
  name: string;
  address?: string;
  contactEmail?: string;
  contactPhone?: string;
  // Create-only: e-conomic number may be supplied by the migrator/sync.
  economicCustomerNumber?: number | null;
  // Provenance (supplied by the migrator; the in-app path stamps these server-side).
  syncedBy?: 'dinero' | 'economic' | 'manual';
  syncedAt?: string | null;
}

export interface UpdateCustomerPayload extends CustomerFields {
  name?: string;
  address?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
}

/** Columns `GET /api/customers` can order by. Server default: `name` ascending. */
export type CustomerListSortBy = "name" | "email" | "phone" | "address" | "vatNumber" | "createdAt";

export interface ListCustomersQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  /** true = Privat, false = Virksomhed; omitted = both. */
  isPerson?: boolean;
  sortBy?: CustomerListSortBy;
  sortDir?: "asc" | "desc";
  /** Integration source (`CustomerDto.syncedBy`); omitted = all. Unknown values are ignored server-side. */
  syncedBy?: "dinero" | "economic" | "manual";
  /** true = only partner customers (the "Rekvireret af" picker). */
  isPartner?: boolean;
  /** Only customers requisitioned by this partner customer. */
  sourcePartnerCustomerId?: string;
}

/** An agreement on the customer as a whole (no anlæg). Price is excl. VAT; commercial data, never log it. */
export interface CreateCustomerServiceAgreementPayload {
  title: string;
  intervalDays: number;
  estimatedDurationMinutes: number;
  startDate: string;
  status: AssetMaintenanceScheduleStatus;
  priceExclVat?: number | null;
  currency?: string | null;
  billToCustomerId?: string | null;
  projectTemplateId?: string | null;
  /** Omitted = on for normal callers (off for the migration actor). */
  generateProjectWhenDue?: boolean;
  /** @deprecated Ignored by the generation sweep; the company lead time is used. */
  projectGenerationLeadDays?: number;
  expiresAt?: string | null;
  id?: string;
}

export class CustomersApi {
  constructor(private readonly http: HttpClient) {}

  list(query: ListCustomersQuery = {}): Promise<ApiResult<PagedCustomers>> {
    return this.http.get<PagedCustomers>("/api/customers", { query: { ...query } });
  }

  get(id: string): Promise<ApiResult<CustomerDto>> {
    return this.http.get<CustomerDto>(`/api/customers/${encodeURIComponent(id)}`);
  }

  /** Customer hub in one round trip: anlæg, agreements, projects, partners. */
  getHub(id: string): Promise<ApiResult<CustomerHubDto>> {
    return this.http.get<CustomerHubDto>(`/api/customers/${encodeURIComponent(id)}/hub`);
  }

  create(payload: CreateCustomerPayload): Promise<ApiResult<CustomerDto>> {
    return this.http.post<CustomerDto>("/api/customers", { body: payload });
  }

  update(id: string, payload: UpdateCustomerPayload): Promise<ApiResult<CustomerDto>> {
    return this.http.patch<CustomerDto>(`/api/customers/${encodeURIComponent(id)}`, { body: payload });
  }

  remove(id: string): Promise<ApiResult<void>> {
    return this.http.delete<void>(`/api/customers/${encodeURIComponent(id)}`);
  }

  listServiceAgreements(id: string): Promise<ApiResult<CustomerServiceAgreementsDto>> {
    return this.http.get<CustomerServiceAgreementsDto>(`/api/customers/${encodeURIComponent(id)}/service-agreements`);
  }

  createServiceAgreement(id: string, payload: CreateCustomerServiceAgreementPayload): Promise<ApiResult<AssetMaintenanceScheduleDto>> {
    return this.http.post<AssetMaintenanceScheduleDto>(`/api/customers/${encodeURIComponent(id)}/service-agreements`, { body: payload });
  }
}
